import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Clock,
  Plus,
  ArrowLeft,
  Download,
  ArrowsClockwise,
  CalendarBlank,
  User,
  CheckCircle,
  PaperPlaneTilt,
  Export,
  Timer,
  Trash,
  PencilSimple,
  ArrowsLeftRight,
  FloppyDisk,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  exported: 'bg-purple-100 text-purple-700',
};

export const TimesheetEditorPage = () => {
  const { id } = useParams();
  const { user, api, isSupervisorPlus } = useAuth();
  const navigate = useNavigate();
  
  const [timesheet, setTimesheet] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [showAddLineDialog, setShowAddLineDialog] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [lineForm, setLineForm] = useState({
    date: '',
    work_order_title: '',
    job_code: '',
    job_description: '',
    regular_hours: 8,
    overtime_hours: 0,
    double_time_hours: 0,
    travel_hours: 0,
    per_diem: 0,
    notes: ''
  });

  const fetchTimesheet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/timesheets/${id}`);
      setTimesheet(response.data?.timesheet);
      setLines(response.data?.lines || []);
    } catch (error) {
      console.error('Failed to fetch timesheet:', error);
      toast.error('Failed to load timesheet');
      navigate('/timesheets');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  const handleAddLine = async () => {
    setSaving(true);
    try {
      await api.post(`/timesheets/${id}/lines`, lineForm);
      toast.success('Line added');
      setShowAddLineDialog(false);
      resetLineForm();
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add line');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLine = async () => {
    if (!editingLine) return;
    setSaving(true);
    try {
      await api.put(`/timesheets/${id}/lines/${editingLine.id}`, lineForm);
      toast.success('Line updated');
      setEditingLine(null);
      resetLineForm();
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update line');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!confirm('Delete this line?')) return;
    try {
      await api.delete(`/timesheets/${id}/lines/${lineId}`);
      toast.success('Line deleted');
      fetchTimesheet();
    } catch (error) {
      toast.error('Failed to delete line');
    }
  };

  const handleSubmit = async () => {
    if (!confirm('Submit this timesheet for approval?')) return;
    try {
      await api.post(`/timesheets/${id}/submit`);
      toast.success('Timesheet submitted');
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    if (!confirm('Approve this timesheet?')) return;
    try {
      await api.post(`/timesheets/${id}/approve`);
      toast.success('Timesheet approved');
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.post(`/timesheets/${id}/export/xlsx`, {}, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Timesheet_${timesheet?.employee_name?.replace(' ', '_')}_${timesheet?.week_ending}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Timesheet exported');
      fetchTimesheet();
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSyncAssignments = async () => {
    setSyncing(true);
    try {
      const response = await api.post(`/timesheets/${id}/sync-assignments`);
      toast.success(response.data?.message || 'Synced from assignments');
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const resetLineForm = () => {
    setLineForm({
      date: '',
      work_order_title: '',
      job_code: '',
      job_description: '',
      regular_hours: 8,
      overtime_hours: 0,
      double_time_hours: 0,
      travel_hours: 0,
      per_diem: 0,
      notes: ''
    });
  };

  const openEditLine = (line) => {
    setEditingLine(line);
    setLineForm({
      date: line.date || '',
      work_order_title: line.work_order_title || '',
      job_code: line.job_code || '',
      job_description: line.job_description || '',
      regular_hours: line.regular_hours || 0,
      overtime_hours: line.overtime_hours || 0,
      double_time_hours: line.double_time_hours || 0,
      travel_hours: line.travel_hours || 0,
      per_diem: line.per_diem || 0,
      notes: line.notes || ''
    });
  };

  const canEdit = timesheet?.status === 'draft';
  const canSubmit = timesheet?.status === 'draft' && lines.length > 0;
  const canApprove = timesheet?.status === 'submitted' && isSupervisorPlus();
  const canExport = (timesheet?.status === 'approved' || timesheet?.status === 'exported') && isSupervisorPlus();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <ArrowsClockwise size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Timesheet not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/timesheets')}>
          Back to Timesheets
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="timesheet-editor-page">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/timesheets')} className="mb-4 -ml-2">
          <ArrowLeft size={18} className="mr-2" />
          Back to Timesheets
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Clock size={28} weight="fill" className="text-orange-600" />
              Timesheet Editor
            </h1>
            <p className="text-slate-600 mt-1">
              Week Ending: {new Date(timesheet.week_ending).toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
              })}
            </p>
          </div>
          
          <div className="flex gap-2">
            {canEdit && (
              <>
                <Button variant="outline" onClick={handleSyncAssignments} disabled={syncing}>
                  <ArrowsLeftRight size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync Assignments
                </Button>
                <Button onClick={() => setShowAddLineDialog(true)}>
                  <Plus size={16} className="mr-2" />
                  Add Line
                </Button>
              </>
            )}
            {canSubmit && (
              <Button onClick={handleSubmit} variant="default">
                <PaperPlaneTilt size={16} className="mr-2" />
                Submit
              </Button>
            )}
            {canApprove && (
              <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle size={16} className="mr-2" />
                Approve
              </Button>
            )}
            {canExport && (
              <Button onClick={handleExport} variant="outline">
                <Download size={16} className="mr-2" />
                Export XLSX
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Timesheet Header Info */}
      <Card className="mb-6 bg-orange-50 border-orange-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-orange-600 font-medium">Employee</p>
              <p className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <User size={18} className="text-slate-400" />
                {timesheet.employee_name}
              </p>
            </div>
            <div>
              <p className="text-sm text-orange-600 font-medium">Station</p>
              <p className="text-lg font-semibold text-slate-900">{timesheet.station || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-orange-600 font-medium">Status</p>
              <Badge className={`${STATUS_COLORS[timesheet.status]} mt-1`}>
                {timesheet.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-orange-600 font-medium">Total Hours</p>
              <p className="text-2xl font-bold text-slate-900">{(timesheet.total_hours || 0).toFixed(1)}</p>
            </div>
            <div>
              <p className="text-sm text-orange-600 font-medium">Per Diem</p>
              <p className="text-2xl font-bold text-slate-900">${(timesheet.total_per_diem || 0).toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hours Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-slate-500">Regular</p>
            <p className="text-2xl font-bold text-slate-900">{(timesheet.total_regular || 0).toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-slate-500">Overtime</p>
            <p className="text-2xl font-bold text-amber-600">{(timesheet.total_overtime || 0).toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-slate-500">Double Time</p>
            <p className="text-2xl font-bold text-red-600">{(timesheet.total_double_time || 0).toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-slate-500">Travel</p>
            <p className="text-2xl font-bold text-blue-600">{(timesheet.total_travel || 0).toFixed(1)}h</p>
          </CardContent>
        </Card>
      </div>

      {/* Lines Table */}
      <Card>
        <CardHeader>
          <CardTitle>Time Entries</CardTitle>
          <CardDescription>
            {lines.length} entr{lines.length !== 1 ? 'ies' : 'y'} for this week
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lines.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CalendarBlank size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No time entries yet</p>
              {canEdit && (
                <Button variant="outline" className="mt-4" onClick={() => setShowAddLineDialog(true)}>
                  Add Your First Entry
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Work Order</TableHead>
                  <TableHead>Job Code</TableHead>
                  <TableHead className="text-right">Regular</TableHead>
                  <TableHead className="text-right">OT</TableHead>
                  <TableHead className="text-right">DT</TableHead>
                  <TableHead className="text-right">Travel</TableHead>
                  <TableHead className="text-right">Per Diem</TableHead>
                  {canEdit && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={line.id} data-testid={`line-row-${index}`}>
                    <TableCell className="font-medium">
                      {new Date(line.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{line.work_order_title || '-'}</TableCell>
                    <TableCell>{line.job_code || '-'}</TableCell>
                    <TableCell className="text-right">{(line.regular_hours || 0).toFixed(1)}</TableCell>
                    <TableCell className="text-right text-amber-600">{(line.overtime_hours || 0).toFixed(1)}</TableCell>
                    <TableCell className="text-right text-red-600">{(line.double_time_hours || 0).toFixed(1)}</TableCell>
                    <TableCell className="text-right text-blue-600">{(line.travel_hours || 0).toFixed(1)}</TableCell>
                    <TableCell className="text-right">${(line.per_diem || 0).toFixed(2)}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditLine(line)}>
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteLine(line.id)} className="text-red-600 hover:text-red-700">
                            <Trash size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Line Dialog */}
      <Dialog open={showAddLineDialog || !!editingLine} onOpenChange={(open) => {
        if (!open) {
          setShowAddLineDialog(false);
          setEditingLine(null);
          resetLineForm();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLine ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle>
            <DialogDescription>
              {editingLine ? 'Update the time entry details' : 'Add a new time entry for this week'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Date</label>
                <Input
                  type="date"
                  value={lineForm.date}
                  onChange={(e) => setLineForm({...lineForm, date: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Job Code</label>
                <Input
                  value={lineForm.job_code}
                  onChange={(e) => setLineForm({...lineForm, job_code: e.target.value})}
                  placeholder="e.g., ELEC-001"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Work Order</label>
              <Input
                value={lineForm.work_order_title}
                onChange={(e) => setLineForm({...lineForm, work_order_title: e.target.value})}
                placeholder="Work order title"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={lineForm.job_description}
                onChange={(e) => setLineForm({...lineForm, job_description: e.target.value})}
                placeholder="Job description"
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-sm font-medium">Regular</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lineForm.regular_hours}
                  onChange={(e) => setLineForm({...lineForm, regular_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">OT</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lineForm.overtime_hours}
                  onChange={(e) => setLineForm({...lineForm, overtime_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">DT</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lineForm.double_time_hours}
                  onChange={(e) => setLineForm({...lineForm, double_time_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Travel</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lineForm.travel_hours}
                  onChange={(e) => setLineForm({...lineForm, travel_hours: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Per Diem ($)</label>
              <Input
                type="number"
                step="0.01"
                value={lineForm.per_diem}
                onChange={(e) => setLineForm({...lineForm, per_diem: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={lineForm.notes}
                onChange={(e) => setLineForm({...lineForm, notes: e.target.value})}
                placeholder="Optional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddLineDialog(false); setEditingLine(null); resetLineForm(); }}>
              Cancel
            </Button>
            <Button onClick={editingLine ? handleUpdateLine : handleAddLine} disabled={saving || !lineForm.date}>
              <FloppyDisk size={16} className="mr-2" />
              {saving ? 'Saving...' : (editingLine ? 'Update' : 'Add Entry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetEditorPage;
